/**
 * Item Image Management Service
 * Handles item image operations with Supabase database
 */

import { supabase } from '../utils/supabase';

export interface ItemImageOperationResult {
  success: boolean;
  data?: any;
  error?: string;
  auth_method?: string;
}

class ItemImageService {
  /**
   * Add an image to an item in the database
   */
  async addItemImage(
    userId: string,
    itemId: string,
    imageUrl: string,
    isPrimary: boolean = false,
    imageOrder?: number
  ): Promise<ItemImageOperationResult> {
    if (!supabase) {
      console.warn('Supabase not configured');
      return { success: false, error: 'Supabase not configured' };
    }

    try {
      const { data, error } = await supabase.rpc('add_item_image', {
        p_user_id: userId,
        p_item_id: itemId,
        p_image_url: imageUrl,
        p_is_primary: isPrimary,
        p_image_order: imageOrder
      });

      if (error) {
        console.error('Error adding item image:', error);
        return { success: false, error: error.message };
      }

      if (data && !data.success) {
        return { success: false, error: data.error };
      }

      return { 
        success: true, 
        data: data,
        auth_method: data?.auth_method 
      };
    } catch (error) {
      console.error('Error calling add_item_image RPC:', error);
      return { success: false, error: 'Failed to add image to database' };
    }
  }

  /**
   * Delete an image from an item
   */
  async deleteItemImage(
    userId: string,
    imageId: string
  ): Promise<ItemImageOperationResult> {
    if (!supabase) {
      console.warn('Supabase not configured');
      return { success: false, error: 'Supabase not configured' };
    }

    try {
      const { data, error } = await supabase.rpc('delete_item_image', {
        p_user_id: userId,
        p_image_id: imageId
      });

      if (error) {
        console.error('Error deleting item image:', error);
        return { success: false, error: error.message };
      }

      if (data && !data.success) {
        return { success: false, error: data.error };
      }

      return { 
        success: true, 
        data: data,
        auth_method: data?.auth_method 
      };
    } catch (error) {
      console.error('Error calling delete_item_image RPC:', error);
      return { success: false, error: 'Failed to delete image from database' };
    }
  }

  /**
   * Set an image as primary for an item
   */
  async setPrimaryItemImage(
    userId: string,
    imageId: string
  ): Promise<ItemImageOperationResult> {
    if (!supabase) {
      console.warn('Supabase not configured');
      return { success: false, error: 'Supabase not configured' };
    }

    try {
      const { data, error } = await supabase.rpc('set_primary_item_image', {
        p_user_id: userId,
        p_image_id: imageId
      });

      if (error) {
        console.error('Error setting primary item image:', error);
        return { success: false, error: error.message };
      }

      if (data && !data.success) {
        return { success: false, error: data.error };
      }

      return { 
        success: true, 
        data: data,
        auth_method: data?.auth_method 
      };
    } catch (error) {
      console.error('Error calling set_primary_item_image RPC:', error);
      return { success: false, error: 'Failed to set primary image in database' };
    }
  }

  /**
   * Extract image ID from database-generated UUID or external service ID
   */
  extractImageId(imageId: string): string {
    // For database UUIDs, return as-is
    // For external service IDs, return as-is
    return imageId;
  }
}

// Export singleton instance
export const itemImageService = new ItemImageService();
export default itemImageService;
